import { z } from 'zod';

/**
 * All features in the application
 * Includes both main app sections and individual feature toggles
 */
export const FEATURES = {
  // Main
  JOURNALS: 'journals',

  // Experimental
  API_REQUEST_LOGGER: 'realtime-api-request-logger',
  ARENA: 'arena',
  DATA_SHARING: 'data-sharing',
  DYNAMIC_BANNER: 'dynamic-banner',
  SCREENSHOT: 'screenshot',
  WORLD_MODEL: 'world_model',
  AXIOM_LIB: 'axiom-lib',
  CONTACTS_INTEGRATION: 'contacts_integration',
  MEETING_APP_DETECTION: 'meeting-app-detection',
  MEETING_IMMEDIATE_AUTO_STOP: 'meeting-immediate-auto-stop',
  MEETING_NOTES_BROWSER_SEGMENTATION: 'meeting-notes-browser-segmentation',
  MEETING_INSTRUCTION_SKIPPED_STATUS: 'meeting-instruction-skipped-status',
  DICTATION: 'dictation',
  DICTATION_AI_CLEANUP: 'dictation-ai-cleanup',
  LOCAL_TRANSCRIPTION: 'local-transcription',
  LOCAL_TRANSCRIPTION_RESIDENT_MODEL: 'local-transcription-resident-model',
  LOCAL_TRANSCRIPTION_POST_PROCESSING: 'local-transcription-post-processing',
  LOCAL_TRANSCRIPTION_DIARIZATION: 'local-transcription-diarization',
  DEEPGRAM_TRANSCRIPTION_DIARIZATION: 'deepgram-transcription-diarization',
  REALTIME_SPEAKER_ATTRIBUTION: 'realtime-speaker-attribution',
  LOCAL_TRANSCRIPTION_DEFAULT: 'local-transcription-default',
  LOCAL_TRANSCRIPTION_ENGINE_PICKER: 'local-transcription-engine-picker',
  LOCAL_TRANSCRIPTION_VAD_GATING: 'local-transcription-vad-gating',
  MEETING_NOTES_CUSTOM_VOCABULARY: 'meeting-notes-custom-vocabulary',
  MEETING_NOTES_ECHO_CANCELLATION: 'meeting-notes-echo-cancellation',
  MEETING_NOTES_LANE_RECOVERY: 'meeting-notes-lane-recovery',
  MEETING_PARTICIPANT_DETECTION: 'meeting-participant-detection',
  MEETING_SITE_GATE_DISABLED: 'meeting_site_gate_disabled',
  MEETING_ASSIST: 'meeting-assist',
  MEETING_ASSIST_POPUPS: 'meeting-assist-popups',
  MEETING_INTENT_DETECTION: 'meeting-intent-detection',
  MEETING_SELECTED_INPUT_SILENT_NOTIFICATION:
    'meeting-selected-input-silent-notification',
  HIVE: 'hive',
  SPRITES: 'sprites',
  ROUTINE_ADVANCED_SCHEDULES: 'routine-advanced-schedules',
  ROUTINE_TRIGGERS: 'routine-triggers',
  ROUTINE_OUTLOOK_TRIGGERS: 'routine-outlook-triggers',
  ROUTINE_WEBHOOK_TRIGGERS: 'routine-webhook-triggers',
  ROUTINE_MEETING_STARTED_TRIGGERS: 'routine-meeting-started-triggers',
  ROUTINE_MEETING_SUMMARIZED_TRIGGERS: 'routine-meeting-summarized-triggers',
  ROUTINE_REACH_OUT: 'routine-reach-out',
  ROUTINE_LIBRARY_FIRST: 'routine-library-first',
  SCHEDULED_RESOURCE_SNAPSHOT_COLLECTION:
    'scheduled-resource-snapshot-collection',
  RESOURCE_SNAPSHOT_INCLUDE_PROCESSES: 'resource-snapshot-include-processes',
  PATTERN_BASED_REDACTION: 'pattern-based-redaction',
  CATEGORY_DOMAINS_SEED_ONLY: 'category-domains-seed-only',
  APPLESCRIPT_URL_EXTRACTION: 'applescript-url-extraction',
  OBSERVED_INTERVALS: 'observed-intervals',
  CONTEXT_KIT_DEBUG_VIEW: 'context-kit-debug-view',
  MCP_INTEGRATIONS: 'mcp-integrations',
  CUSTOM_MCP_INTEGRATIONS: 'custom-mcp-integrations',
  CUSTOM_LOCAL_MCP: 'custom-local-mcp',
  FREE_PLAN_INTEGRATIONS: 'free-plan-integrations',
  EMAIL_ATTRIBUTION: 'email-attribution',
  SNAPSHOT_DEBUG_DUMP: 'snapshot-debug-dump',
  VOICE_MODE: 'voice-mode',
  VOICE_MODE_LIVE: 'voice-mode-live',
  ACTION_CARDS_FEED: 'action-cards-feed',
  FEED_CARD_DEBUG_PANEL: 'feed-card-debug-panel',
  FEED_COMPOSER: 'feed-composer',
  FEED_LAB: 'feed-lab',
  GMAIL_FEED_AUTO_ARCHIVE: 'gmail-feed-auto-archive',
  PROACTIVE_AGENT: 'proactive-agent',
  PROACTIVE_CARDS_IN_CHANNELS: 'proactive-cards-in-channels',
  AUTO_REVIEW: 'auto-review',
  AXON_COMPUTER_USE: 'axon-computer-use',
  AXON_OUTLOOK_LOCAL: 'axon-outlook-local',
  OPTIN_APP_ONBOARDING: 'optin-app-onboarding',
  ONBOARDING_ACCESSIBILITY_SKIP: 'onboarding-accessibility-skip',
  AGENTS_UX: 'agents-ux',
  WEB_ONBOARDING: 'web-onboarding',
  WEB_ONBOARDING_ALWAYS: 'web-onboarding-always',
  WEB_ONBOARDING_FEED: 'web-onboarding-feed',
  WEB_ONBOARDING_REQUIRE_CONNECTION: 'web-onboarding-require-connection',
  DISABLE_SYNC_FINGERPRINT_SKIP: 'disable-sync-fingerprint-skip',
  DISABLE_STALE_DONE_EVENT_GUARD: 'disable-stale-done-event-guard',
  DISABLE_AXON_KEEPS_DEVICE_REACHABLE: 'disable-axon-keeps-device-reachable',
  UNIFIED_INTEGRATIONS_MANAGEMENT: 'unified-integrations-management',
  HOME_INTEGRATION_SUGGESTIONS: 'home-integration-suggestions',
  FEED_CONNECT_APPS_CARD: 'feed-connect-apps-card',
  INTEGRATION_SUGGESTIONS_CAMPAIGN: 'integration-suggestions-campaign',
  ON_DEVICE_INTEGRATION_SUGGESTIONS: 'on-device-integration-suggestions',
  LOCAL_TERMINAL: 'local-terminal',
  CODING_AGENTS: 'coding-agents',
  QUEUED_MESSAGES: 'queued-messages',
  NON_INTERRUPTING_STEERING: 'non-interrupting-steering',
  PEOPLE_WIKI: 'people-wiki',
  EXTERNAL_MEETING_NOTES: 'external-meeting-notes',
  CHATGPT_EXPORT: 'chatgpt-export',
  AGENT_INBOX: 'agent-inbox',
  SHARE_WITH_PEOPLE: 'share-with-people',
} as const;

/**
 * Features that have associated payload data from PostHog
 */
export const PAYLOAD_FEATURES = {
  DYNAMIC_BANNER: 'dynamic-banner',
  ENABLED_LB_FEATURES: 'enabled_lb_features',
  APP_PARSING_CONFIGURATION: 'app_parsing_configuration',
  MEETING_APP_BUNDLE_IDS: 'meeting_app_bundle_ids',
  MEETING_SITES: 'meeting_sites',
  MEETING_SELECTED_INPUT_SILENT_NOTIFICATION:
    'meeting-selected-input-silent-notification',
  SCHEDULED_RESOURCE_SNAPSHOT_COLLECTION:
    'scheduled-resource-snapshot-collection',
  REMOTE_DEBUG_LOGGING: 'remote_debug_logging',
  PRICING_PAGE_COPY: 'pricing-page-copy',
  FIRST_CHAT_RECOMMENDATIONS: 'first-chat-recommendations',
  SIDEBAR_GHOST_CHATS: 'sidebar-ghost-chats',
} as const;

export type Feature = (typeof FEATURES)[keyof typeof FEATURES];
export type PayloadFeature =
  (typeof PAYLOAD_FEATURES)[keyof typeof PAYLOAD_FEATURES];

export const FeatureFlagsPayloadSchema = z.object({
  flags: z.string(),
});

/**
 * Feature flag value can be:
 * - boolean: simple on/off flag
 * - unknown: payload flag with arbitrary data (e.g., config objects)
 */
export const FeatureFlagValueSchema = z.union([z.boolean(), z.unknown()]);

export const FeatureFlagsRecordSchema = z.record(
  z.string(),
  FeatureFlagValueSchema
);

export type FeatureFlagsRecord = z.infer<typeof FeatureFlagsRecordSchema>;
